import React, { useState } from 'react';
import * as XLSX from 'xlsx';
import { downloadContent } from '../../../../../../../Utils/downloadUtils/DownloadUtils';
import { useAppStore } from '../../../../../../../GlobalStore/GlobalStore.ts';
import { getFormattedEnvName } from '../../../../../../../Utils/misc/envNameUtils.ts';

interface TableComponentProps {
    children: React.ReactNode;
    index: number;
    node?: any;
    props?: any;
}

const TableComponent: React.FC<TableComponentProps> = ({ children, index, ...props }) => {
    const [isDownloading, setIsDownloading] = useState(false);
    const environment = useAppStore((state: any) => state.environmentObj.environment);
    const canDownload = getFormattedEnvName(environment) === 'Web';

    const downloadTable = async (tableNode: any) => {
        if (isDownloading) return;

        try {
            setIsDownloading(true);

            // Generate Excel file data
            const workbook = XLSX.utils.table_to_book(tableNode);
            const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });

            // Convert to base64
            const base64 = btoa(String.fromCharCode(...new Uint8Array(excelBuffer)));
            const dataUrl = `data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${base64}`;

            // Use environment-aware download
            await downloadContent({
                content: dataUrl,
                filename: `table-${index}.xlsx`,
                type: 'xlsx'
            });
        } catch (error) {
            console.error('Failed to download table:', error);
        } finally {
            setIsDownloading(false);
        }
    };

    const handleDownloadPress = (event: React.MouseEvent | React.TouchEvent) => {
        event.preventDefault();
        event.stopPropagation();

        const tableNode = document.getElementById(tableId);
        if (tableNode) {
            void downloadTable(tableNode);
        }
    };

    const tableId = `table-${index}-${Math.random().toString(36).substr(2, 9)}`;

    return (
        <div className="rounded-lg overflow-hidden -mx-1.5 border border-neutral-400 dark:border-neutral-700">
            <div className="py-2 px-3 overflow-x-auto bg-neutral-300 dark:bg-neutral-950 mx-auto rounded-t-lg min-w-60 flex justify-end">
                {canDownload && (
                    <button
                        className={`pointer-events-auto touch-manipulation text-xs text-neutral-800 dark:text-white font-sans px-2.5 py-0.5 border border-neutral-400 dark:border-neutral-400 rounded-xl bg-white dark:bg-transparent active:scale-[0.98] ${isDownloading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-neutral-200 dark:hover:bg-gray-700'}`}
                        onClick={handleDownloadPress}
                        onTouchEnd={handleDownloadPress}
                        disabled={isDownloading}
                        type="button"
                    >
                        {isDownloading ? 'Downloading...' : 'Download'}
                    </button>
                )}
            </div>
            <div className="overflow-x-auto bg-neutral-100 dark:bg-neutral-950 bg-opacity-50 dark:bg-opacity-50 rounded-b-lg">
                <table id={tableId} className="my-2 text-sm min-w-full divide-neutral-400 dark:divide-neutral-900" {...props}>
                    {children}
                </table>
            </div>
        </div>
    );
};

export default TableComponent;
